import React from "react";
import { Box, Grid, Typography } from "@mui/material";
import { PALETTE, Criteria, criteriaHeadline } from "../types";

// Renders the OBJECTIVE-AWARE criteria block. Never re-derives from
// a percent-correct assumption — reads only `criteria` (backend DTO).
// Percent Correct: shows threshold as N% + acrossSessions + minTrials.
// Frequency: shows Min/Max Frequency + occurrences unit + sessions.
const MasteryCriteriaSummary: React.FC<{ criteria: Criteria; label?: boolean }> = ({ criteria, label = true }) => {
  const c = criteria;
  const isFrequency = c.family === "frequency";
  const headline = criteriaHeadline(c);
  const unitLabel = isFrequency ? (c.unit && c.unit !== "count" ? c.unit : "occurrences") : "%";
  const thresholdCellValue = c.threshold !== null
    ? (isFrequency ? `${c.threshold} ${unitLabel}` : `${c.threshold}%`)
    : "—";
  const thresholdCellLabel = isFrequency
    ? (c.direction === "reduce" ? "Maximum" : "Minimum")
    : (c.direction === "reduce" ? "Max Percent Correct" : "Percent Correct");

  return (
    <Box>
      {label && (
        <Typography sx={{ fontSize: 10.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: PALETTE.slate, mb: 1 }}>
          Mastery Criteria
        </Typography>
      )}
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: PALETTE.ink, mb: 1 }}>{headline}</Typography>
      <Grid container spacing={1.25}>
        <Grid item xs={4}>
          <Cell label={thresholdCellLabel} value={thresholdCellValue} />
        </Grid>
        <Grid item xs={4}>
          <Cell label="Across" value={c.sessions !== null ? `${c.sessions} session${c.sessions === 1 ? "" : "s"}` : "—"} />
        </Grid>
        <Grid item xs={4}>
          {isFrequency ? (
            <Cell label="Metric" value="Frequency" />
          ) : (
            <Cell label="Min. trials / session" value={c.min_trials_per_session !== null ? String(c.min_trials_per_session) : "—"} />
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

const Cell: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ p: 1.25, border: `1px solid ${PALETTE.border}`, borderRadius: 1.5, bgcolor: "#fafcff", height: "100%" }}>
    <Typography sx={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: PALETTE.slate, mb: 0.25 }}>{label}</Typography>
    <Typography sx={{ fontSize: 14, fontWeight: 800, color: PALETTE.ink }}>{value}</Typography>
  </Box>
);

export default MasteryCriteriaSummary;
